import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StoryEvent } from '../types';
import { useIntersectionObserver, useParallax } from '../hooks/useScrollAnimation';

const OurStory: React.FC = () => {
  const { ref, hasIntersected } = useIntersectionObserver();
  const parallaxOffset = useParallax(0.1);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());

  const toggleEvent = (eventId: string) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const storyEvents: StoryEvent[] = [
    {
      id: '1',
      title: 'El principio',
      date: '2013',
      preview: 'Nos conocimos en 2013, siendo un par de chavales en primero de carrera que no tenían ni idea de la vida (ni de lo que se nos venía encima).',
      description: ''
    },
    {
      id: '2',
      title: 'Empieza lo bueno',
      date: 'Un año después',
      preview: 'Un año y pico después empezó lo bueno, aunque pronto descubrimos que nuestra ambición y la lucha por cumplir nuestros sueños nos obligarían a meter la vida en una maleta.',
      description: ''
    },
    {
      id: '3',
      title: 'A prueba de distancia',
      date: 'Lyon y Alemania, 2022',
      preview: 'Tras superar la distancia primero en la etapa de Loren en Lyon y, más tarde, en su primer año en Alemania en 2022, lo que se había afianzado en Sevilla demostró estar a prueba de cualquier distancia.',
      description: ''
    },
    {
      id: '4',
      title: 'Juntos en tierras alemanas',
      date: 'Verano de 2023',
      preview: 'En el verano de 2023, Ale se mudó con Loren a tierras alemanas. Esa convivencia nos confirmó lo que ya sentíamos: que somos el uno para el otro.',
      description: ''
    },
    {
      id: '5',
      title: 'El momento clave',
      date: '15 de junio',
      preview: 'Un 15 de junio en un bar de Múnich. Tras alguna que otra indirecta y presión por parte de Ale —porque ella tenía muy claro que ya tocaba—, Loren se lanzó a pedírselo (y ella, claro, dijo que sí).',
      description: ''
    },
    {
      id: '6',
      title: 'El pistoletazo de salida',
      date: '17 de octubre de 2026',
      preview: 'Ya en Sevilla, entre papeleos, maletas y con una ilusión enorme, estamos listos. El próximo 17 de octubre es el pistoletazo de salida a nuestra nueva vida conjunta. Y la fiesta no estará completa sin vosotros. ¡Os esperamos!',
      description: ''
    },
  ];

  return (
    <section
      id="our-story"
      className="relative py-10 bg-gradient-to-br from-cream-100 to-cream-200 overflow-hidden"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          style={{ transform: `translateY(${parallaxOffset}px)` }}
          className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-olive-200 to-forest-300 rounded-full blur-3xl opacity-30"
        />
        <motion.div
          style={{ transform: `translateY(${-parallaxOffset}px)` }}
          className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-olive-200 to-forest-300 rounded-full blur-3xl opacity-20"
        />
      </div>

      <div className="container-max section-padding relative z-10">
        <motion.div
          ref={ref as any}
          initial={{ opacity: 0, y: 50 }}
          animate={hasIntersected ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-8"
        >
          <motion.h2
            className="font-wasted text-4xl font-normal text-wedding-primary mb-6"
            whileHover={{ scale: 1.02 }}
          >
            Nuestra historia
          </motion.h2>
          <motion.p
            className="font-avenir text-lg text-wedding-secondary max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={hasIntersected ? { opacity: 1 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Parece mentira que hayan pasado tantos años desde que cruzamos nuestras vidas por primera vez. Entre maletas, distancias, mudanzas y mil sueños compartidos, hemos ido construyendo una historia preciosa que nos ha traído hasta el día de hoy.
            <br />          
            ¿Quieres ver cómo empezó todo y cómo hemos llegado hasta aquí? 
            <br />
            ¡Acompáñanos en este viaje!
          </motion.p>
        </motion.div>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-sand-400 via-olive-500 to-forest-600 rounded-full"></div>
          
          {/* Animated dots on timeline */}
          {storyEvents.map((_, index) => (
            <motion.div
              key={index}
              className="absolute left-1/2 transform -translate-x-1/2 w-3 h-3 bg-gradient-to-br from-olive-400 to-forest-600 rounded-full shadow-lg"
              style={{
                top: `${storyEvents.length > 1 ? (index / (storyEvents.length - 1)) * 80 + 10 : 50}%`
              }}
              initial={{ scale: 0 }}
              animate={hasIntersected ? { scale: 1 } : {}}
              transition={{ delay: index * 0.2, duration: 0.5 }}
              whileHover={{ scale: 1.5 }}
            />
          ))}

          {/* Story events */}
          <div className="space-y-24">
            {storyEvents.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
                animate={hasIntersected ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: index * 0.3, duration: 0.8 }}
                className={`flex items-center ${
                  index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                }`}
              >
                <div className="flex-1 px-8">
                  <motion.div
                    whileHover={{ scale: 1.02, y: -5 }}
                    className={`card-wedding p-8 ${
                      index % 2 === 0 ? 'ml-auto' : 'mr-auto'
                    } max-w-lg`}
                  >
                    <motion.h3
                      className="font-avenir text-2xl font-medium italic text-wedding-primary mb-2"
                      whileHover={{ color: '#504B38' }}
                    >
                      {event.title}
                    </motion.h3>
                    <motion.p
                      className="font-avenir text-wedding-accent font-medium mb-4"
                      initial={{ opacity: 0 }}
                      animate={hasIntersected ? { opacity: 1 } : {}}
                      transition={{ delay: index * 0.3 + 0.2 }}
                    >
                      {event.date}
                    </motion.p>
                    <div className="font-avenir text-forest-700 leading-relaxed">
                      <AnimatePresence mode="wait">
                        {expandedEvents.has(event.id) ? (
                          <motion.p
                            key="full"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            {event.preview}{event.description}
                          </motion.p>
                        ) : (
                          <motion.p
                            key="preview"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            {event.preview}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>
                    
                    {event.description && (
                      <motion.button
                        onClick={() => toggleEvent(event.id)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="mt-4 flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-olive-400 to-forest-600 text-white shadow-md hover:shadow-lg transition-shadow"
                        aria-label={expandedEvents.has(event.id) ? "Contraer" : "Expandir"}
                      >
                        <motion.span
                          animate={{ rotate: expandedEvents.has(event.id) ? 45 : 0 }}
                          transition={{ duration: 0.3 }}
                          className="text-xl font-bold"
                        >
                          +
                        </motion.span>
                      </motion.button>
                    )}
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurStory; 
